import type { ComponentChildren } from "preact";
import { useRef } from "react";
import { shortAddr } from "../../wallets/lib/format";
import { clock } from "../lib/format";
import { Link, openWallet, walletHref } from "../lib/nav";
import { useApiStatus } from "../lib/status";
import { FLAGS_REBUILDING } from "../lib/flags";

type Tone = "up" | "dn" | "warn" | "flag" | "info" | "dim";

const CHIP_TONES: Record<Tone, string> = {
	up: "bg-up/12 text-up",
	dn: "bg-dn/12 text-dn",
	warn: "bg-warn/12 text-warn",
	flag: "bg-flag/12 text-flag",
	info: "bg-info/12 text-info",
	dim: "bg-line text-dim",
};

const outcomeTone = (name: string): Tone => {
	const n = name.toLowerCase();
	return n === "yes" ? "up" : n === "no" ? "dn" : "dim";
};

/** Bare text form of OutcomeTag, for phones where the outcome column is dropped. */
export function OutcomeLead({ name }: { name: string }) {
	const tone = outcomeTone(name);
	return <span className={tone === "up" ? "text-up" : tone === "dn" ? "text-dn" : "text-fg"}>{name} · </span>;
}

/** Yes/No get their colours; named outcomes stay neutral because neither side is "good". */
export function OutcomeTag({ name }: { name: string }) {
	return (
		<span className={`inline-block max-w-full truncate px-1 align-middle text-label ${CHIP_TONES[outcomeTone(name)]}`} title={name}>
			{name}
		</span>
	);
}

export function Chip({ tone = "dim", title, children }: { tone?: Tone; title?: string; children: ComponentChildren }) {
	return (
		<span title={title} className={`inline-block whitespace-nowrap px-1 text-label ${CHIP_TONES[tone]}`}>
			{children}
		</span>
	);
}

/** Polymarket fills unset names with address-like strings; a raw hash is noise in a cell. */
export function walletText(address: string, label?: string | null): string {
	return label && !/^(0x)?[0-9a-f]{30,}/i.test(label) ? label : shortAddr(address);
}

export function WalletLink({ address, label }: { address: string; label?: string | null }) {
	const isAddress = /^0x[0-9a-fA-F]{40}$/.test(address);
	const text = walletText(address, label);
	if (!isAddress) return <span className="text-dim">{text}</span>;
	return (
		<a
			href={walletHref(address)}
			title={address}
			className="lnk-quiet inline-block max-w-full truncate align-middle text-fg max-sm:py-2.5"
			onClick={(e: MouseEvent) => {
				e.stopPropagation();
				if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
				e.preventDefault();
				openWallet(address);
			}}
		>
			{text}
		</a>
	);
}

export function Toggle({
	on,
	onClick,
	children,
	title,
	disabled,
}: {
	on: boolean;
	onClick: () => void;
	children: ComponentChildren;
	title?: string;
	disabled?: boolean;
}) {
	return (
		<button type="button" aria-pressed={on} title={title} disabled={disabled} onClick={onClick} className="ctl">
			{children}
		</button>
	);
}

export function Select({
	label,
	value,
	options,
	onChange,
}: {
	label: string;
	value: string;
	options: { value: string; label: string }[];
	onChange: (v: string) => void;
}) {
	return (
		<select
			aria-label={label}
			value={value}
			onChange={(e) => onChange((e.currentTarget as HTMLSelectElement).value)}
			className="ctl min-w-0 max-w-[9.5rem]"
		>
			{options.map((o) => (
				<option key={o.value} value={o.value}>
					{o.label}
				</option>
			))}
		</select>
	);
}

export function SearchBox({
	value,
	onChange,
	placeholder,
}: {
	value: string;
	onChange: (v: string) => void;
	placeholder: string;
}) {
	return (
		<div className="relative basis-full sm:w-56 sm:basis-auto">
			<span aria-hidden="true" className="absolute top-1/2 left-2 -translate-y-1/2 text-body text-mute">
				/
			</span>
			<input
				data-search
				type="search"
				aria-label={placeholder}
				placeholder={placeholder}
				value={value}
				onInput={(e) => onChange((e.currentTarget as HTMLInputElement).value)}
				className="ctl w-full pl-5"
			/>
		</div>
	);
}

/** Says why flag data is empty during a rebuild, so an empty table is not read as "nothing suspicious". */
export function RebuildNote() {
	if (!FLAGS_REBUILDING) return null;
	return (
		<p className="shrink-0 border border-line bg-panel px-2 py-1 text-body text-fg">
			Flags are being rebuilt from on-chain fills, so this list is empty for now. An empty list does not mean nothing was flagged.
		</p>
	);
}

/** One line under a grid: what is shown and where it came from. `className` can reserve the height it wraps to on a phone. */
export function GridNote({ children, className = "" }: { children: ComponentChildren; className?: string }) {
	return <div className={`shrink-0 border-t border-line bg-ink px-2 py-1 text-label text-mute ${className}`}>{children}</div>;
}

export function Panel({ children }: { children: ComponentChildren }) {
	return <div className="flex min-h-0 flex-1 flex-col border border-line">{children}</div>;
}

/** A titled block inside a page or the drawer. */
export function Section({
	title,
	note,
	right,
	id,
	children,
}: {
	title: ComponentChildren;
	note?: ComponentChildren;
	right?: ComponentChildren;
	id?: string;
	children: ComponentChildren;
}) {
	return (
		<section id={id} className="min-w-0 border border-line bg-panel">
			<div className="flex min-h-7 flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-line px-2 py-1">
				<h2 className="text-label font-bold uppercase tracking-wide text-bright">{title}</h2>
				{note ? <span className="text-label text-mute">{note}</span> : null}
				{right}
			</div>
			{children}
		</section>
	);
}

/** One figure. Sits in a StatGrid, which draws the hairlines between tiles. */
export function Stat({
	label,
	value,
	sub,
	tone,
	title,
	className,
}: {
	label: string;
	value: ComponentChildren;
	sub?: ComponentChildren;
	tone?: string;
	title?: string;
	className?: string;
}) {
	return (
		<div title={title} className={`min-w-0 bg-panel px-2 py-1 ${className ?? ""}`}>
			<div className="truncate text-label text-dim">{label}</div>
			<div className={`num truncate text-title font-bold ${tone ?? "text-bright"}`}>{value}</div>
			{sub ? <div className="truncate text-label text-mute">{sub}</div> : null}
		</div>
	);
}

export function StatGrid({ className, children }: { className: string; children: ComponentChildren }) {
	return <div className={`grid gap-px border border-line bg-line ${className}`}>{children}</div>;
}

export interface TabItem<T extends string> {
	id: T;
	label: string;
}

/** Keyboard tablist: arrows and Home/End move between tabs. */
export function Tabs<T extends string>({
	items,
	value,
	onChange,
	label,
}: {
	items: TabItem<T>[];
	value: T;
	onChange: (id: T) => void;
	label: string;
}) {
	const list = useRef<HTMLDivElement>(null);
	const onKey = (e: KeyboardEvent) => {
		const at = items.findIndex((t) => t.id === value);
		const next =
			e.key === "ArrowRight"
				? (at + 1) % items.length
				: e.key === "ArrowLeft"
					? (at - 1 + items.length) % items.length
					: e.key === "Home"
						? 0
						: e.key === "End"
							? items.length - 1
							: -1;
		const item = items[next];
		if (!item) return;
		e.preventDefault();
		onChange(item.id);
		list.current?.querySelectorAll<HTMLElement>('[role="tab"]')[next]?.focus();
	};
	return (
		<div ref={list} role="tablist" aria-label={label} onKeyDown={onKey} className="no-scrollbar flex gap-px overflow-x-auto">
			{items.map((t) => (
				<button
					key={t.id}
					type="button"
					role="tab"
					aria-selected={value === t.id}
					tabIndex={value === t.id ? 0 : -1}
					onClick={() => onChange(t.id)}
					className={`tab-t ${value === t.id ? "is-on" : ""}`}
				>
					{t.label}
				</button>
			))}
		</div>
	);
}

/** Previous / next for server-paged lists. */
export function Pager({
	page,
	pages,
	total,
	hasPrev,
	hasNext,
	busy,
	onPrev,
	onNext,
}: {
	page: number;
	pages: number;
	total: number;
	hasPrev: boolean;
	hasNext: boolean;
	busy?: boolean;
	onPrev: () => void;
	onNext: () => void;
}) {
	return (
		<div className="flex items-center justify-between gap-2 border-t border-line px-2 py-1 text-label text-dim">
			<button type="button" className="ctl" disabled={busy || !hasPrev} onClick={onPrev} aria-label="Previous page">
				← prev
			</button>
			<span className="num">
				page {page} of {pages} · {total.toLocaleString("en-US")} total
			</span>
			<button type="button" className="ctl" disabled={busy || !hasNext} onClick={onNext} aria-label="Next page">
				next →
			</button>
		</div>
	);
}

/** Placeholder lines for a list. Same row height as the rows they stand in for. */
export function SkeletonRows({ rows = 5, cols = 3 }: { rows?: number; cols?: number }) {
	return (
		<div aria-hidden="true">
			{Array.from({ length: rows }, (_, r) => (
				<div key={r} className="flex h-(--row-h) items-center gap-3 border-b border-line/50 px-2 last:border-b-0">
					{Array.from({ length: cols }, (_, c) => (
						<div key={c} className={`skeleton-bar h-2 ${c === 0 ? "flex-1" : "w-12"}`} style={c === 0 ? { maxWidth: `${50 + ((r * 13) % 40)}%` } : undefined} />
					))}
				</div>
			))}
		</div>
	);
}

/** Placeholder rows for a .tbl body; `widths` is one Tailwind width per column. */
export function SkeletonTbody({ rows, widths }: { rows: number; widths: (string | null)[] }) {
	return (
		<tbody aria-hidden="true">
			{Array.from({ length: rows }, (_, r) => (
				<tr key={r}>
					{widths.map((w, c) => (
						<td key={c} className={w === null ? "max-sm:hidden" : undefined}>
							<div className={`skeleton-bar h-2 ${w ?? "w-10"}`} />
						</td>
					))}
				</tr>
			))}
		</tbody>
	);
}

/**
 * Empty, error and API-down all use this frame, so an absent list always reads
 * the same way: what state it is, what that means, what to do next.
 */
export function StatePanel({
	tag,
	tone = "dim",
	title,
	children,
	action,
}: {
	tag?: string;
	tone?: "dn" | "warn" | "dim";
	title: ComponentChildren;
	children?: ComponentChildren;
	action?: ComponentChildren;
}) {
	const rule = tone === "dn" ? "border-dn" : tone === "warn" ? "border-warn" : "border-line-strong";
	return (
		<div role="status" className="p-2">
			<div className={`max-w-3xl border-l-2 py-0.5 pl-3 text-body ${rule}`}>
				<div className="flex flex-wrap items-center gap-x-2 gap-y-1">
					{tag ? <Chip tone={tone}>{tag}</Chip> : null}
					<span className="text-data text-bright">{title}</span>
				</div>
				{children ? <div className="mt-1 space-y-1 text-dim">{children}</div> : null}
				{action ? <div className="mt-2 flex flex-wrap items-center gap-2 text-mute">{action}</div> : null}
			</div>
		</div>
	);
}

const FALLBACK_VIEWS = [
	{ href: "/", label: "unusual markets" },
	{ href: "/whales", label: "large trades" },
	{ href: "/smart-money", label: "top traders" },
	{ href: "/smart-money/copy", label: "copy candidates" },
	{ href: "/segments", label: "segments" },
];

/** Shown in place of rows when a view depends on api.polygains.com and it is not answering. */
export function ApiDown({ what }: { what: string }) {
	const api = useApiStatus();
	return (
		<StatePanel
			tag="API DOWN"
			tone="dn"
			title="api.polygains.com is not responding."
			action={
				<>
					<button type="button" onClick={api.recheck} className="ctl">
						retry now
					</button>
					<span className="num">
						{api.checkedAt ? `last check ${clock(api.checkedAt)}` : "checking"}
						{api.nextCheckAt ? ` · rechecks at ${clock(api.nextCheckAt)}` : ""}
					</span>
				</>
			}
		>
			<p>That API serves {what}, so there is nothing to show until it returns.</p>
			<p>
				These views do not need it:{" "}
				{FALLBACK_VIEWS.map((v, i) => (
					<span key={v.href}>
						{i > 0 ? " · " : ""}
						<Link href={v.href} className="lnk">
							{v.label}
						</Link>
					</span>
				))}
			</p>
		</StatePanel>
	);
}

export function SourceError({ source, onRetry }: { source: string; onRetry?: () => void }) {
	return (
		<StatePanel
			tag="ERROR"
			tone="dn"
			title={`Could not load from ${source}.`}
			action={
				onRetry ? (
					<button type="button" onClick={onRetry} className="ctl">
						retry
					</button>
				) : undefined
			}
		/>
	);
}

export function Empty({ children }: { children: ComponentChildren }) {
	return <StatePanel title={<span className="text-body text-dim">{children}</span>} />;
}
